import React from 'react';
import type { MagneticWallType } from './editorSlice';
import {
  getAttributorType,
  getClosestAttribute,
} from './formats/utils/attributors';
import { useQuill } from './QuillContext';
import {
  addStatusByAttributor,
  removeStatuses,
} from './useSyncMagneticWallState';
import { useIsMagneticWallItemVisible } from '../post/useVisibleMagneticWallItems';
import ImageBlot from './formats/ImageBlot';
import Slot from '../components/Slot';

interface AttributeHandlerProps {
  children: React.ReactNode;
  onAttributeClick: (
    attribute: NonNullable<ReturnType<typeof getClosestAttribute>>,
    type: MagneticWallType,
  ) => void;
  asChild?: boolean;
}

const AttributeHandler = React.forwardRef<
  HTMLDivElement,
  AttributeHandlerProps
>(({ children, onAttributeClick, asChild }, ref) => {
  const { quill } = useQuill();
  const isMagneticWallItemVisible = useIsMagneticWallItemVisible();

  const handleMouseOver = (e: React.MouseEvent) => {
    if (!quill) return;

    const { target } = e;
    if (target instanceof HTMLElement) {
      removeStatuses(quill, ['highlight']);

      const attribute = getClosestAttribute(target);

      if (attribute && isMagneticWallItemVisible(attribute.id)) {
        addStatusByAttributor(
          quill,
          attribute.attributor,
          attribute.id,
          'highlight',
        );
      }
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;

    if (
      !window?.getSelection?.()?.toString().length ||
      quill?.scroll.find(target, true) instanceof ImageBlot
    ) {
      const attribute = getClosestAttribute(target);

      if (attribute && isMagneticWallItemVisible(attribute.id)) {
        const type = getAttributorType(attribute.attributor);
        onAttributeClick(attribute, type);
      }
    }
  };

  const Component = asChild ? Slot : 'div';
  return (
    <Component ref={ref} onMouseOver={handleMouseOver} onClick={handleClick}>
      {children}
    </Component>
  );
});

export default AttributeHandler;
